import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdRadioButton,
  CmdSimpleSelect,
  CmdChipSelect,
  CmdLabel,
  CmdInput,
  CmdCheckboxGroup,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService from '../../Services/FormikValidatorService';
import Section, { IFormSectionButton } from '../Forms/Section';
import { reportSettingsOptionsR08 } from './ReportSettings';
import { IOption } from '../Forms/CmdField';
import usePageWithForm from '../../hooks/usePageWithForm';
import Calendar from '../Forms/ReactHookFormFields/Calendar';
import {
  getBluecoins,
  getMinDatepickerValue,
  getValidation,
  handleDatepickerValidation,
  handleReportSettings,
} from './ReportService';
import { IReportItem } from './types';
import { transformErrors } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

interface ReportR08InputForm {
  reportId: string;
  bluecoinIds: string[];
  reportSettings: string[];
  format: string;
  date: Date[];
  excludeRecordsShorterThan: string;
}

interface ReportR08Props {
  handleReportId: (id: string) => void;
  handleMainButton: (indexId: string, value: boolean) => void;
  selectedReportId: string;
  reportsList: IReportItem[];
  buttonDisabled: boolean;
  handleDownload: (url: string, values: any) => void;
}

export default function ReportR08(props: ReportR08Props) {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<any>({});
  const parseDateToday0 = new Date(new Date().setHours(0, 0, 0, 0));
  const parseDateToday24 = new Date(new Date().setHours(23, 59, 0, 0));
  const [bluecoinOptions, setBluecoinOptions] = React.useState<IOption[]>([]);
  const [reportRange, setReportRange] = React.useState('');
  const [reportActionUrl, setReportActionUrl] = React.useState('');
  const { trackEvent } = useAnalytics();

  const getReportSettingsOptions = () => {
    return reportSettingsOptionsR08.map((item: string) => {
      return { value: item, item: f({ id: `reports.R07.${item}` }) };
    });
  };

  const validateFields = (values: any) => {
    if (values) {
      props.handleMainButton('isDisabled', false);
      props.handleMainButton('hasError', false);
    }
    let formErrors: any = {};
    if (!validation) {
      return formErrors;
    }
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    const message = handleDatepickerValidation(values?.date, props.selectedReportId, reportRange);
    if (message.length > 0) {
      formErrors.date = f({
        id: message,
      });
    }
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<ReportR08InputForm>({
    mode: 'onChange',
    defaultValues: {
      reportId: props.selectedReportId,
      format: 'xlsx',
      date: [parseDateToday0, parseDateToday24],
      reportSettings: [],
      bluecoinIds: [],
      excludeRecordsShorterThan: '5 ',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = async (values: any) => {
    if (reportActionUrl) {
      props.handleMainButton('isDisabled', true);
      const newValues = { ...values };
      Object.values(reportSettingsOptionsR08).forEach((item: string) => {
        newValues[item] = !!values.reportSettings.includes(item);
      });
      delete newValues.reportSettings;
      newValues.dateFrom = values.date[0];
      newValues.dateTo = values.date[1];
      delete newValues.date;
      props.handleDownload(reportActionUrl, newValues);
      trackEvent(['reports', 'click', 'Report R08 submitted']);
    }
  };

  const getReportValidation = async () => {
    const responseData = await getValidation(props.selectedReportId);
    if (responseData) {
      setReportActionUrl(responseData?.actionUrl || '');
      setReportRange(responseData?.range || '');
      setValidation(responseData.form);
      setValue(
        'reportSettings',
        handleReportSettings(responseData.userSettings, reportSettingsOptionsR08)
      );
    }
  };

  const getBluecoinsData = async () => {
    const options: IOption[] | false = await getBluecoins(props.selectedReportId);
    if (options) {
      setBluecoinOptions(options);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['reportR08Validation'],
        queryFn: () => getReportValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['reportR08Customers'],
        queryFn: () => getBluecoinsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const downloadButton: IFormSectionButton = {
    title: f({ id: 'reports.download' }),
    tooltip: f({ id: 'reports.download' }),
    disabled: props.buttonDisabled,
    buttonCallback: handleSubmit(onSubmit),
  };

  return (
    <div className="w-page new-css-wrapper" data-cy="reports-wrapper">
      <form
        className="form-horizontal"
        onSubmit={handleSubmit(onSubmit)}
        style={{ paddingTop: '30px' }}
        data-cy="reports-form"
      >
        <div className="container-fluid" data-cy="reports-container">
          <div data-cy="reports-sections">
            <div className="row" data-cy="reports-row">
              <div className="col-xxl-6" data-cy="reports-col">
                <Section
                  id="alarms-basic-information"
                  title={f({ id: 'alarms.section.basicInformation' })}
                  buttons={[downloadButton]}
                >
                  <Controller
                    name="reportId"
                    control={control}
                    rules={{ required: errors.reportId ? errors.reportId.message : undefined }}
                    render={({ field }) => (
                      <CmdSimpleSelect
                        {...field}
                        name="reportId"
                        id="reportId"
                        value={props.selectedReportId}
                        label={f({ id: 'reports.reportId' })}
                        introOption={f({ id: 'suppliersForm.choose' })}
                        options={props.reportsList}
                        required={true}
                        onChangeCallback={(value: string) => {
                          setValue('reportId', value);
                          props.handleReportId(value);
                          trackEvent(['reports', 'change', 'Report R08 ID changed']);
                        }}
                        error={errors.reportId?.message}
                        touched={true}
                      />
                    )}
                  />
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: errors.date ? errors.date.message : undefined }}
                    render={({ field }) => (
                      <Calendar
                        {...field}
                        id="date"
                        name="date"
                        label={f({ id: 'datepicker.range' })}
                        required={true}
                        allowedRange={[
                          getMinDatepickerValue(),
                          new Date(new Date().setHours(23, 59, 59, 999)),
                        ]}
                        note="(dd.mm.rrrr hh:mm)"
                        mode="range"
                        disabledItems={['THIS_YEAR', 'PREVIOUS_YEAR']}
                        error={errors.date?.message}
                        touched={true}
                        onChangeCallback={(value: Date[]) => {
                          setValue('date', value);
                          trigger('date');
                          trackEvent(['reports', 'change', `Report ${value} changed`]);
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="format"
                    control={control}
                    rules={{ required: errors.format ? errors.format.message : undefined }}
                    render={({ field }) => (
                      <CmdRadioButton
                        {...field}
                        id="format"
                        name="format"
                        label={f({ id: 'reports.format' })}
                        options={[{ item: '.xlsx', value: 'xlsx' }]}
                        required={true}
                        disabled={true}
                      />
                    )}
                  />
                  <Controller
                    name="bluecoinIds"
                    control={control}
                    rules={{
                      required: errors.bluecoinIds ? errors.bluecoinIds.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdChipSelect
                        {...field}
                        id="bluecoinIds"
                        name="bluecoinIds"
                        label={f({ id: 'menu.bluecoins' })}
                        introOption={f({ id: 'form.choose' })}
                        options={bluecoinOptions}
                        required={true}
                        placeholder={f({ id: 'form.choose' })}
                        searchPlaceholder={f({ id: 'form.search' })}
                        selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                        resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                        searchTooltip={f({
                          id: 'alarms.section.vehicles.driverId.search.tooltip',
                        })}
                        selectButtonTooltip={f({
                          id: 'alarms.section.vehicles.driverId.selectButton.tooltip',
                        })}
                        resetButtonTooltip={f({
                          id: 'alarms.section.vehicles.driverId.resetButton.tooltip',
                        })}
                        bossCheckbox={f({ id: 'alarms.modal.timeFrame.button.selectAll' })}
                        error={errors.bluecoinIds?.message}
                        touched={true}
                        value={watch('bluecoinIds')}
                        setValue={(value: string[]) => {
                          setValue('bluecoinIds', value);
                          trigger('bluecoinIds');
                          trackEvent(['reports', 'change', 'Report R08 bluecoins changed']);
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="excludeRecordsShorterThan"
                    control={control}
                    rules={{
                      required: errors.excludeRecordsShorterThan
                        ? errors.excludeRecordsShorterThan.message
                        : undefined,
                    }}
                    render={({ field }) => (
                      <CmdInput
                        {...field}
                        id="excludeRecordsShorterThan"
                        name="excludeRecordsShorterThan"
                        label={f({ id: 'reports.excludeRecordsShorterThan' })}
                        currency="min"
                        value={watch('excludeRecordsShorterThan')}
                        type="text"
                        onChangeCallback={(value: string) => {
                          setValue('excludeRecordsShorterThan', value);
                          trackEvent(['reports', 'change', 'Report R08 excludeRecordsShorterThan']);
                        }}
                        error={errors.excludeRecordsShorterThan?.message}
                        touched={true}
                      />
                    )}
                  />
                </Section>
              </div>

              <div className="col-xxl-6" data-cy="reports-col">
                <CmdBox id="reports-sending-settings" title={f({ id: 'reports.reportSettings' })}>
                  {reportSettingsOptionsR08.length > 0 ? (
                    <Controller
                      name="reportSettings"
                      control={control}
                      rules={{
                        required: errors.reportSettings ? errors.reportSettings.message : undefined,
                      }}
                      render={({ field }) => (
                        <CmdCheckboxGroup
                          {...field}
                          key="reportSettings"
                          id="reportSettings"
                          name="reportSettings"
                          value={watch('reportSettings')}
                          label={f({ id: 'reports.parametersSetting' })}
                          options={getReportSettingsOptions()}
                          gridTemplateColumns=" 1fr 1fr 1fr "
                          bossCheckbox={f({ id: 'reports.chooseAll' })}
                          required={false}
                          setValue={(value: string[]) => {
                            setValue('reportSettings', value);
                            trigger('reportSettings');
                            trackEvent(['reports', 'change', 'Report R08 reportSettings changed']);
                          }}
                        />
                      )}
                    />
                  ) : (
                    <CmdLabel
                      title={f({ id: 'reports.parametersSetting' })}
                      value={f({ id: 'reports.noParametersAvailable' })}
                      isLabelInTwoRows={true}
                    />
                  )}
                </CmdBox>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
